import * as THREE from 'three'
import type { PathSegment } from '../types'

const SEGMENT_COLORS: Record<string, number> = {
  corridor: 0x60a5fa,   // blue
  escalier: 0xfbbf24,   // yellow
  ascenseur: 0xa78bfa,  // purple
}

const HANDLE_RADIUS = 0.15
const SEGMENT_HEIGHT = 0.1 // Y offset above floor
const HANDLE_Y = 0.15

/** Connectivity: 0 = isolated (red), 1 = partial (blue), 2 = full (green) */
function getConnectivity(seg: PathSegment, allSegments: PathSegment[]): [boolean, boolean] {
  const TOLERANCE = 0.1
  let startConnected = false
  let endConnected = false

  for (const other of allSegments) {
    if (other.id === seg.id) continue
    if (other.floor !== seg.floor) continue

    const points = [
      { x: other.start_x, z: other.start_z },
      { x: other.end_x, z: other.end_z },
    ]
    for (const p of points) {
      if (Math.hypot(p.x - seg.start_x, p.z - seg.start_z) < TOLERANCE) startConnected = true
      if (Math.hypot(p.x - seg.end_x, p.z - seg.end_z) < TOLERANCE) endConnected = true
    }
  }

  return [startConnected, endConnected]
}

export class PathSegmentRenderer {
  group: THREE.Group
  private segmentGroups: Map<string, THREE.Group> = new Map()
  private handleMeshes: THREE.Mesh[] = []

  constructor() {
    this.group = new THREE.Group()
    this.group.name = 'path-segments'
  }

  setSegments(segments: PathSegment[], floorGroups: Map<number, THREE.Group>) {
    this.clear()

    for (const seg of segments) {
      const segGroup = this.createSegment(seg, segments)
      this.segmentGroups.set(seg.id, segGroup)

      const floorGroup = floorGroups.get(seg.floor)
      if (floorGroup) {
        floorGroup.add(segGroup)
      } else {
        this.group.add(segGroup)
      }
    }
  }

  private createSegment(seg: PathSegment, allSegments: PathSegment[]): THREE.Group {
    const group = new THREE.Group()
    group.name = `pathseg-${seg.id}`
    group.userData = { type: 'pathSegment', segmentId: seg.id }

    const dx = seg.end_x - seg.start_x
    const dz = seg.end_z - seg.start_z
    const length = Math.hypot(dx, dz)
    const midX = (seg.start_x + seg.end_x) / 2
    const midZ = (seg.start_z + seg.end_z) / 2
    const angle = Math.atan2(dx, dz)

    // Line mesh (PlaneGeometry rotated flat)
    const segColor = SEGMENT_COLORS[seg.type ?? 'corridor'] ?? SEGMENT_COLORS.corridor!
    const lineGeo = new THREE.PlaneGeometry(seg.width * 0.3, length)
    lineGeo.rotateX(-Math.PI / 2) // Lay flat on XZ
    const lineMat = new THREE.MeshPhongMaterial({
      color: segColor,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
      depthWrite: false,
    })
    const lineMesh = new THREE.Mesh(lineGeo, lineMat)
    lineMesh.position.set(midX, SEGMENT_HEIGHT, midZ)
    lineMesh.rotation.y = angle
    lineMesh.name = `pathseg-line-${seg.id}`
    lineMesh.userData = { type: 'pathSegment', segmentId: seg.id }
    group.add(lineMesh)

    // Handles (start = green, end = red, transition = blue)
    const [startConnected, endConnected] = getConnectivity(seg, allSegments)

    const startColor = seg.start_is_transition ? 0x60a5fa :
      startConnected ? 0x4ade80 : 0xef4444
    const endColor = seg.end_is_transition ? 0x60a5fa :
      endConnected ? 0x4ade80 : 0xef4444

    const startHandle = this.createHandle(startColor, 'start', seg.id)
    startHandle.position.set(seg.start_x, HANDLE_Y, seg.start_z)
    group.add(startHandle)
    this.handleMeshes.push(startHandle)

    const endHandle = this.createHandle(endColor, 'end', seg.id)
    endHandle.position.set(seg.end_x, HANDLE_Y, seg.end_z)
    group.add(endHandle)
    this.handleMeshes.push(endHandle)

    return group
  }

  private createHandle(color: number, endpoint: 'start' | 'end', segmentId: string): THREE.Mesh {
    const geo = new THREE.SphereGeometry(HANDLE_RADIUS, 12, 12)
    const mat = new THREE.MeshPhongMaterial({ color, emissive: color, emissiveIntensity: 0.3 })
    const mesh = new THREE.Mesh(geo, mat)
    mesh.name = `pathseg-handle-${endpoint}-${segmentId}`
    mesh.userData = { type: 'pathSegmentHandle', endpoint, segmentId }
    return mesh
  }

  selectSegment(segmentId: string | null) {
    for (const [id, group] of this.segmentGroups) {
      group.traverse((obj) => {
        if (obj instanceof THREE.Mesh && obj.name.startsWith('pathseg-line-')) {
          const mat = obj.material as THREE.MeshPhongMaterial
          if (id === segmentId) {
            mat.emissive.setHex(0x444444)
            mat.opacity = 0.9
          } else {
            mat.emissive.setHex(0x000000)
            mat.opacity = 0.6
          }
        }
      })
    }
  }

  setVisible(visible: boolean) {
    // Set visibility on all segment groups
    for (const group of this.segmentGroups.values()) {
      group.visible = visible
    }
    this.group.visible = visible
  }

  getHandleMeshes(): THREE.Mesh[] {
    return this.handleMeshes
  }

  private clear() {
    for (const group of this.segmentGroups.values()) {
      group.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose()
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m) => m.dispose())
          } else {
            obj.material.dispose()
          }
        }
      })
      group.parent?.remove(group)
    }
    this.segmentGroups.clear()
    this.handleMeshes = []
  }

  dispose() {
    this.clear()
  }
}
